import { Controller } from "@hotwired/stimulus";

// Postcode + huisnummer → straat + plaats (PDOK Locatieserver via Rails-proxy).
// Optioneel: backoffice-fallback vult ook naam/email/phone bij bekende klanten.
//
// Gebruik:
//   data-controller="postcode-lookup"
//   data-postcode-lookup-backoffice-value="true"   // optioneel
//   data-postcode-lookup-debounce-value="400"      // optioneel
//
// Targets (allemaal optioneel; lookup gebeurt als minimaal zipcode+housenumber aanwezig zijn):
//   zipcode, housenumber, country (input/select), street, city
//   loading, success (elementen die getoond/verborgen worden via display)
//   firstname, lastname, email, phone (backoffice-fallback auto-fill — alleen als veld leeg is)
export default class extends Controller {
  static targets = [
    "zipcode", "housenumber", "country",
    "street", "city",
    "firstname", "lastname", "email", "phone",
    "loading", "success"
  ];

  static values = {
    url: { type: String, default: "/services/dutch_zipcode_lookup" },
    backoffice: { type: Boolean, default: false },
    debounce: { type: Number, default: 400 },
    nlCountryId: { type: String, default: "172" }
  };

  connect() {
    this.timer = null;
    this.setLoading(false);
    this.setSuccess(false);
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer);
  }

  schedule() {
    if (this.timer) clearTimeout(this.timer);
    this.setSuccess(false);

    if (!this.hasZipcodeTarget || !this.hasHousenumberTarget) return;

    const zip = (this.zipcodeTarget.value || "").trim();
    const housenr = this.housenumberTarget.value || "";

    if (this.hasCountryTarget && this.countryTarget.value && this.countryTarget.value !== this.nlCountryIdValue) {
      this.setLoading(false);
      return;
    }

    if (!/^\d{4} ?[A-Za-z]{2}$/.test(zip) || !/\d/.test(housenr.toString())) {
      this.setLoading(false);
      return;
    }

    this.setLoading(true);
    this.timer = setTimeout(() => this.lookup(zip, housenr), this.debounceValue);
  }

  async lookup(zip, housenr) {
    const cleanZip = zip.replace(/ /g, "");
    const matches = housenr.toString().match(/^\d+|\d+\b|\d+(?=\w)/g);
    const strippedNumber = matches ? matches[0] : "xxx";

    let url = `${this.urlValue}/${encodeURIComponent(cleanZip)}/${encodeURIComponent(strippedNumber)}`;
    if (this.backofficeValue) url += "?backoffice=true";

    try {
      const resp = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json" }
      });
      const json = await resp.json();
      this.setLoading(false);

      // Backoffice klant-fallback: vult bestaande velden alleen als ze leeg zijn
      // (medewerker moet niet ineens overschreven worden tijdens typen).
      if ("id" in json) {
        if (this.hasFirstnameTarget && json.firstname) this.setIfEmpty(this.firstnameTarget, json.firstname);
        // AT-1283: het formulier heeft geen tussenvoegselveld, dus dat hoort bij de achternaam.
        const surname = [json.middlename, json.lastname].filter(Boolean).join(" ");
        if (this.hasLastnameTarget && surname) this.setIfEmpty(this.lastnameTarget, surname);
        if (this.hasEmailTarget && json.email) this.setIfEmpty(this.emailTarget, json.email);
        if (this.hasPhoneTarget && json.phone) this.setIfEmpty(this.phoneTarget, json.phone);
      }

      if (json.street && json.street.length > 0) {
        if (this.hasStreetTarget) this.streetTarget.value = json.street;
        if (this.hasCityTarget) this.cityTarget.value = json.city;
        this.setSuccess(true);
      } else {
        if (this.hasStreetTarget) this.streetTarget.value = "";
        if (this.hasCityTarget) this.cityTarget.value = "";
        this.setSuccess(false);
      }
    } catch (_err) {
      this.setLoading(false);
      this.setSuccess(false);
    }
  }

  setIfEmpty(el, value) {
    if (!el.value || el.value.trim() === "") {
      el.value = value;
    }
  }

  setLoading(on) {
    if (this.hasLoadingTarget) this.loadingTarget.style.display = on ? "" : "none";
  }

  setSuccess(on) {
    if (this.hasSuccessTarget) this.successTarget.style.display = on ? "" : "none";
  }
}
